/// <reference types="google.maps" />
import { useEffect, useRef, useState, useCallback } from 'react'
import { loadGoogleMapsScript, waitForGoogleMaps, isGoogleMapsLoaded } from '@lib/utils/googleMaps'

export interface AddressComponents {
  street_number?: string
  route?: string
  locality?: string
  neighborhood?: string
  sublocality?: string
  sublocality_level_1?: string
  postal_town?: string
  administrative_area_level_1?: string
  administrative_area_level_2?: string
  postal_code?: string
  country?: string
  formatted_address?: string
  [key: string]: string | undefined
}

export interface PlaceResult {
  address_components: AddressComponents
  formatted_address: string
  geometry?: google.maps.places.PlaceGeometry
  partial_match?: boolean
}

interface UseGooglePlacesAutocompleteOptions {
  onPlaceSelect?: (place: PlaceResult) => void
  countryRestriction?: string | string[]
  types?: string[]
  fields?: string[]
  enabled?: boolean
}

/**
 * React hook for Google Places Autocomplete
 * @param options - Configuration options
 * @returns Autocomplete instance and loading state
 */
export function useGooglePlacesAutocomplete(
  options: UseGooglePlacesAutocompleteOptions = {}
) {
  const {
    onPlaceSelect,
    countryRestriction,
    types = ['address'],
    fields = ['address_components', 'formatted_address', 'geometry'],
    enabled = true,
  } = options

  const inputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const initializationAttemptedRef = useRef(false)
  
  // Store onPlaceSelect in a ref to avoid re-initialization
  const onPlaceSelectRef = useRef(onPlaceSelect)
  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect
  }, [onPlaceSelect])

  // Load Google Maps script proactively on mount
  useEffect(() => {
    if (!enabled) return

    // Check if already loaded
    if (isGoogleMapsLoaded()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing React state to the external Google Maps SDK load status on mount (intentional external-system sync)
      setIsReady(true)
      setIsLoading(false)
      return
    }

    // Start loading immediately
    setIsLoading(true)
    loadGoogleMapsScript()
      .then(() => waitForGoogleMaps())
      .then(() => {
        setIsReady(true)
        setIsLoading(false)
      })
      .catch((error) => {
        console.error('Failed to load Google Maps API:', error)
        setIsLoading(false)
      })
  }, [enabled])

  /**
   * Extract address components from Google Place result
   */
  const extractAddressComponents = useCallback(
    (place: google.maps.places.PlaceResult): AddressComponents => {
      const components: AddressComponents = {}

      if (place.address_components) {
        place.address_components.forEach((component) => {
          const types = component.types

          if (types.includes('street_number')) {
            components.street_number = component.long_name
          }
          if (types.includes('route')) {
            components.route = component.long_name
          }
          if (types.includes('locality')) {
            components.locality = component.long_name
          }
          if (types.includes('neighborhood')) {
            components.neighborhood = component.long_name
          }
          if (types.includes('sublocality')) {
            components.sublocality = component.long_name
          }
          if (types.includes('sublocality_level_1')) {
            components.sublocality_level_1 = component.long_name
          }
          if (types.includes('postal_town')) {
            components.postal_town = component.long_name
          }
          if (types.includes('administrative_area_level_1')) {
            components.administrative_area_level_1 = component.short_name
          }
          if (types.includes('administrative_area_level_2')) {
            components.administrative_area_level_2 = component.long_name
          }
          if (types.includes('postal_code')) {
            components.postal_code = component.long_name
          }
          if (types.includes('country')) {
            components.country = component.short_name
          }
        })
      }

      components.formatted_address = place.formatted_address || ''

      return components
    },
    []
  )

  /**
   * Initialize autocomplete - can be called multiple times safely
   */
  const initializeAutocomplete = useCallback(() => {
    if (!inputRef.current || !enabled) {
      return false
    }

    // Check if Google Maps is ready
    if (!window.google?.maps?.places?.Autocomplete) {
      return false
    }

    // If already initialized, don't reinitialize
    if (autocompleteRef.current && initializationAttemptedRef.current) {
      return true
    }

    try {
      // Cleanup existing autocomplete if any
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
        autocompleteRef.current = null
        initializationAttemptedRef.current = false
      }

      const autocomplete = new google.maps.places.Autocomplete(
        inputRef.current,
        {
          componentRestrictions: countryRestriction
            ? { country: countryRestriction as string | string[] }
            : undefined,
          types,
          fields,
        }
      )

      autocomplete.setFields(fields)

      // Listen for place selection
      google.maps.event.addListener(
        autocomplete,
        'place_changed',
        () => {
          const place = autocomplete.getPlace()
          const handler = onPlaceSelectRef.current

          if (
            place &&
            place.address_components &&
            place.address_components.length > 0 &&
            handler
          ) {
            handler({
              address_components: extractAddressComponents(place),
              formatted_address: place.formatted_address || '',
              geometry: place.geometry,
              partial_match: (place as { partial_match?: boolean }).partial_match,
            })
          }
        }
      )

      autocompleteRef.current = autocomplete
      initializationAttemptedRef.current = true
      return true
    } catch (error) {
      console.error('Error initializing autocomplete:', error)
      return false
    }
  }, [enabled, countryRestriction, types, fields, extractAddressComponents])

  // Initialize autocomplete when ready and input is available
  useEffect(() => {
    if (isReady && inputRef.current && !autocompleteRef.current) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        if (inputRef.current && !autocompleteRef.current && isGoogleMapsLoaded()) {
          initializeAutocomplete()
        }
      })
    }
  }, [isReady, initializeAutocomplete])

  // Also check if Google Maps was loaded by another component
  useEffect(() => {
    if (!isReady && !isLoading && isGoogleMapsLoaded()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing React state to the external Google Maps SDK load status (loaded by another component); intentional external-system sync
      setIsReady(true)
      setIsLoading(false)
    }
  }, [isReady, isLoading])

  // Handle focus/click event to ensure autocomplete is initialized
  const handleFocus = useCallback(() => {
    // Check if Google Maps is already loaded (might have been loaded by another component)
    if (isGoogleMapsLoaded() && !isReady) {
      setIsReady(true)
      setIsLoading(false)
      // Initialize immediately if input is available
      if (inputRef.current && !autocompleteRef.current) {
        setTimeout(() => initializeAutocomplete(), 50)
      }
      return
    }

    // If Google Maps is ready, ensure autocomplete is initialized
    if (isGoogleMapsLoaded() && inputRef.current && !autocompleteRef.current) {
      const initialized = initializeAutocomplete()
      if (!initialized) {
        // If initialization failed, wait a bit and try again
        setTimeout(() => {
          initializeAutocomplete()
        }, 100)
      }
    } else if (!isGoogleMapsLoaded() && !isLoading) {
      // If not loaded and not loading, start loading
      setIsLoading(true)
      loadGoogleMapsScript()
        .then(() => waitForGoogleMaps())
        .then(() => {
          setIsReady(true)
          setIsLoading(false)
          // Initialize after script loads
          if (inputRef.current) {
            setTimeout(() => {
              initializeAutocomplete()
            }, 100)
          }
        })
        .catch((error) => {
          console.error('Failed to load Google Maps API:', error)
          setIsLoading(false)
        })
    }
  }, [isReady, isLoading, initializeAutocomplete])

  return {
    inputRef,
    isLoading,
    isReady,
    handleFocus,
    initializeAutocomplete,
  }
}

